import type proto from 'MagicDanmaku/assets/proto/danmaku_proto.d.ts';

import { DegradedType } from '@/degraded/constants';

export type IUserInfo = proto.native.IUserInfo;
export type IDanmakuSetting = proto.native.IDanmakuSetting;
export type IAppendDMSegment = proto.native.IAppendDMSegment;
export type IDMComment = proto.com.tencent.qqlive.protocol.pb.IDMComment;
export type IDMRichDataRsp = proto.trpc.message.danmu_richdata.IDMRichDataRsp;
export type IOperationDMInfo = proto.com.tencent.qqlive.protocol.pb.IOperationDMInfo;
export type IGetDMEffectConfigRsp = proto.com.tencent.qqlive.protocol.pb.IGetDMEffectConfigRsp;
export type ProtoCCClickOperationType = proto.cocos.CCClickOperationType;

/**
 * 分片弹幕类型
 */
export enum AppendDMSegmentType {
  /** 保留字段 */
  APPEND_DM_SEGMENT_TYPE_UNSPECIFIED = 0,
  /** 普通弹幕分片 */
  APPEND_DM_SEGMENT_TYPE_NORMAL = 1,
  /** 用户发表弹幕分片 */
  APPEND_DM_SEGMENT_TYPE_USER_PUBLISH = 2,
  /** 运营弹幕分片 */
  APPEND_DM_SEGMENT_TYPE_OPERATION = 3,
}

/**
 * 运营弹幕类型
 */
export enum OperationDMType {
  /** 未定义类型 */
  OPERATION_DM_TYPE_UNKNOWN = 0,
  /** 默认类型，全局兜底 */
  OPERATION_DM_TYPE_DEFAULT = 1,
  /** 人群包类型 */
  OPERATION_DM_TYPE_USERPACKAGE = 2,
  /** 运营配置引导弹幕类型 */
  OPERATION_DM_TYPE_OPERATION = 3,
  /** 时间点运营弹幕 */
  OPERATION_DM_TYPE_TIMEPOINT = 4,
}

export enum DMOperationType {
  OPERATION_INVSLID = -1,
  /** 默认操作 */
  OPERATION_DEFAULT = 0,
  /** 时间点运营弹幕类型 */
  OPERATION_TIME_POINT = 1,
  /** 发布引导弹幕类型 */
  OPERATION_PUBLISH_GUIDE = 2,
}

/**
 * 运营弹幕活动类型
 */
export enum OperationDMActivityType {
  /** 无点击 */
  OPERATION_DM_ACTIVITY_TYPE_NO_RESPOND = 0,
  /** 全屏活动 */
  OPERATION_DM_ACTIVITY_TYPE_FULL_SCREEN = 1,
  /** 分屏活动 */
  OPERATION_DM_ACTIVITY_TYPE_SPLIT_SCREEN = 2,
  /** 弹幕发布器 */
  OPERATION_DM_ACTIVITY_TYPE_OPEN_PUBLISHER = 3,
  /** 弹幕设置 */
  OPERATION_DM_ACTIVITY_TYPE_OPEN_SETTINGS = 4,
  /** 预约 */
  OPERATION_DM_ACTIVITY_TYPE_APPOINTMENT = 5,
  /** 点赞 */
  OPERATION_DM_ACTIVITY_TYPE_LIKE = 6,
  /** 取消点赞 */
  OPERATION_DM_ACTIVITY_TYPE_UNLIKE = 7,
  /** 投票 */
  OPERATION_DM_ACTIVITY_TYPE_VOTE = 8,
  /** 不拉起发布器直接发弹幕 */
  OPERATION_DM_ACTIVITY_TYPE_PUBLISH = 9,
  /** 全屏动画特效 */
  OPERATION_DM_ACTIVITY_TYPE_FULL_PAG_ANIMATION = 10,
  /** 全屏弹幕特效 */
  OPERATION_DM_ACTIVITY_TYPE_DANMAKU_EFFECT = 11,
  /** 打开弹幕开关 */
  OPERATION_DM_ACTIVITY_TYPE_OPEN_DANMAKU_SWITCH = 12,
  /** 打卡弹幕 */
  OPERATION_DM_ACTIVITY_TYPE_PUNCH = 13,
  /** 弹幕发布器样式面板 */
  OPERATION_DM_ACTIVITY_TYPE_PUBLISHER_STYLE = 14,
  /** 调研弹幕做调研请求 */
  OPERATION_DM_ACTIVITY_TYPE_SURVEY = 15,
  /** 展示TOAST */
  OPERATION_DM_ACTIVITY_TYPE_SHOW_TOAST = 16,
  /** 展示调研BUBBLE */
  OPERATION_DM_ACTIVITY_TYPE_SHOW_SURVEY_BUBBLE = 17,
  /** 调研弹幕发布器 */
  OPERATION_DM_ACTIVITY_TYPE_SURVEY_PUBLISHER = 18,
  /** 刷屏计数弹幕 */
  OPERATION_DM_ACTIVITY_TYPE_FLOOD_COUNT = 19,
  /** 星标 */
  OPERATION_DM_ACTIVITY_TYPE_STAR = 20,
}

/**
 * 支持的弹幕大类型
 */
export enum SupportDmType {
  /** 默认类型（包括全局兜底的头条） */
  OPERATION_DM_TYPE_DEFAULT = OperationDMType.OPERATION_DM_TYPE_DEFAULT,
  /** 时间点弹幕 */
  OPERATION_DM_TYPE_TIMEPOINT = OperationDMType.OPERATION_DM_TYPE_TIMEPOINT,
}

/**
 * 支持的弹幕的点击动作小类型
 * 暂时支持投票同时点击动作是直接发布
 */
export enum SupportOperationDmType {
  /** 投票 */
  OPERATION_DM_ACTIVITY_TYPE_VOTE = OperationDMActivityType.OPERATION_DM_ACTIVITY_TYPE_VOTE,
  /** 直接发布 */
  OPERATION_DM_ACTIVITY_TYPE_PUBLISH = OperationDMActivityType.OPERATION_DM_ACTIVITY_TYPE_PUBLISH,
}

export enum MsgName {
  /** 用户信息 */
  USER_INFO = 'UserInfo',
  /** 强插：头条弹幕、刷屏、投票等 */
  OPERATION = 'Operation',
  /** 强插：用户发送弹幕假显 */
  OPERATION_POST = 'OperationPost',
  /** 弹幕相关，开始、暂停、停止 */
  PLAYER_MSG = 'PlayerMsg',
  /** 弹幕减速用的消息 */
  LIVE_DELTA_TIME_CONFIG = 'LiveDeltaTimeConfig',
  /** 富文本 */
  DM_RICH_DATA_RSP = 'DMRichDataRsp',
  /** 特效数据 */
  DANMAKU_EFFECT = 'DanmakuEffect',
  /** CC native 设置 */
  DANMAKU_SETTING = 'DanmakuSetting',
  /** 展示弹幕分片 */
  APPEND_DM_SEGMENT = 'AppendDMSegment',
  /** 弹幕引擎性能监控开启相关 */
  TAB_INFO = 'TABInfo',
  /** CC 在接收到这个消息后才会向 SDK 发送请求 TAB 相关的信息 */
  VIDEO_ENVIRONMENT_CONFIG = 'VideoEnvironmentConfig',
}

export enum EventType {
  /** frame 加载完成 */
  FRAME_LOADED = 'FRAME_LOADED',
  /** frame 加载失败 */
  FRAME_LOAD_FAILED = DegradedType.FRAME_LOAD_FAILED,
  /** frame 加载失败 */
  FRAME_LOAD_OVERTIME = DegradedType.FRAME_LOAD_OVERTIME,
  /** 心跳 */
  HEART_BEAT = 'HEART_BEAT',
  /** frame 心跳丢失 */
  FRAME_LOST_HEART_BEAT = DegradedType.FRAME_LOST_HEART_BEAT,
  /** CC应用加载完成 */
  CC_LOADED = 'CC_LOADED',
  /** CC引擎加载失败 */
  CC_LOAD_FAILED = DegradedType.CC_LOAD_FAILED,
  /** CC引擎加载超时 */
  CC_LOAD_OVERTIME = DegradedType.CC_LOAD_OVERTIME,
  /** CC引擎暂停主循环 */
  CC_PAUSE = 'CC_PAUSE',
  /** CC引擎恢复主循环 */
  CC_RESUME = 'CC_RESUME',
  /** CC引擎运行 */
  CC_RUN = 'CC_RUN',
  /** CC场景激活 */
  CC_SCENE_ACTIVE = 'CC_SCENE_ACTIVE',
  /** 发送给CC引擎的消息 */
  CC_MSG = 'CC_MSG',
  /** 页面事件 */
  HTML_EVENT = 'HTML_EVENT',
  /** 窗口大小改变 */
  RESIZE = 'RESIZE',
  /** 异步消息类型 */
  CALLBACK = 'CALLBACK',
  /** 帧率 */
  FPS = 'FPS',
  /** webgl 上下文丢失 */
  WEBGL_CONTEXT_LOST = 'WEBGL_CONTEXT_LOST',
}

export enum PlayerEventType {
  PLAYER_EVENT_TYPE_UNSPECIFIED = 0,
  PLAYER_EVENT_TYPE_PLAY = 1, // 弹幕开始加载，一般情况只需要调用一次就够了
  PLAYER_EVENT_TYPE_RESUME = 2, // 弹幕从暂停状态恢复
  PLAYER_EVENT_TYPE_PAUSE = 3, // 弹幕暂停
  PLAYER_EVENT_TYPE_STOP = 4,
  PLAYER_EVENT_TYPE_PLAY_TIME_CHANGE = 5,
  PLAYER_EVENT_TYPE_VID_CHANGE = 6,
  PLAYER_EVENT_TYPE_SEEK = 7, // seek事件，eg、拖拽动作条
}

export enum CCClickConsumeResultType {
  UNCONSUMED = 0,
  CONSUMED = 1,
  NEEDHANDLE = 2,
}

export enum CCClickOperationType {
  OUTER = 1, // 需要特殊处理Result中的operation字段
  LIKE = 2, // 点赞
  COPY = 4, // 复制
  REPORT = 5, // 举报
  LOGIN = 7, // 未登录
}

/**
 * CC TAB配置，在引擎初始化完成时，通过消息：MsgName.TAB_INFO 同步给引擎侧即可开启性能监控
 * 由引擎侧提供，然后写死在前端代码里
 */
export const TAB_INFO = {
  dm_cc_cutoff_monitor_config: JSON.stringify({ enable: 1, checkFrequency: 12000, sampleRate: 2 }),
  dm_cc_sync_monitor_config: JSON.stringify({ enable: 1, checkFrequency: 12000, sampleRate: 2 }),
  dm_perf_collector_config: JSON.stringify({ enable: 1, frequency: 10000, sample_rate: 2 }),
};

/** 下列事件不打印日志(触发较频繁，会导致日志较大) */
export const DISABLE_LOG_EVENT_MAP = [EventType.HTML_EVENT, EventType.HEART_BEAT];

/** 因为弹幕设置在两个版本下并不一致，所以将原始数据记录下来，方便不同版本使用 */
export type IDanmakuSettingExtend = IDanmakuSetting & {
  originData: {
    // 字体原始大小
    textSize: number;
    // 播放器高度
    playerHeight: number;
  };
};

// eslint-disable-next-line @typescript-eslint/naming-convention
export declare const _MDW_: {
  /** CC暴露的协议，用来编解码消息 */
  proto?: typeof proto;
  /** 接受CC消息 */
  onMessage?: (msg: proto.native.ICocosNativeMessage) => unknown;
  /** 发送CC消息 */
  sendMessage?: (msg: proto.native.ICocosNativeMessage) => unknown;
};
